import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import EventMap from '../components/EventMap';
import { 
  User, 
  Calendar, 
  CheckCircle, 
  Users, 
  X,
  ArrowUpRightIcon,
  Link2
} from 'lucide-react';
import { supabase } from '../lib/supabase';

const Dashboard = () => {
  const { user, updateUser, fetchUserProfile } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    bio: '',
    links: '',
    clubs: [],
    tech_interests: [],
    dietary_preference: 'none',
    avatar_url: '',
    public_profile: false
  });
 


  // new states for controlled chip inputs
  const [newClub, setNewClub] = useState('');
  const [newTech, setNewTech] = useState('');
  const venues = [
    {
      id: 1,
      name: 'Harvard Science Center',
      lat: 42.3760,
      lng: -71.1167,
      address: '1 Oxford St, Cambridge, MA 02138'
    }
  ];
  const defaultPersonalChecklist = useMemo(() => ([
    { key: 'housing', title: 'Figured out housing', done: false },
    { key: 'transportation', title: 'Figured out transportation', done: false },
    { key: 'schedule', title: 'Accustomed myself with the schedule', done: false },
    { key: 'mentorship', title: 'Signed up for mentorship circle', done: false }
  ]), []);
  const [personalChecklist, setPersonalChecklist] = useState(defaultPersonalChecklist);

// Avatar upload state
const [avatarFile, setAvatarFile] = useState(null);
const [avatarPreview, setAvatarPreview] = useState('');
const [avatarUploading, setAvatarUploading] = useState(false);
const [loading, setLoading] = useState(false);

// ✅ Prevent memory leaks from blob previews
useEffect(() => {
  return () => {
    if (avatarPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(avatarPreview);
    }
  };
}, [avatarPreview]);


// helper: get initials if no avatar
const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// helper: upload avatar to Supabase Storage + return public URL
const uploadAvatar = async (file, userId) => {
  // You need a bucket named "avatars" in Supabase Storage
  // and policies allowing authenticated users to upload/read.
  const ext = file.name.split('.').pop();
  const filePath = `${userId}/avatar.${ext}`;


  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
      contentType: file.type
    });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
  return data.publicUrl; // store this in your profile table
};



// Checklist-style action items with completion state
const actionItems = [

  { title: 'Add a short bio', link: '/dashboard', done: !!user?.bio },
  { 
    title: 'Set dietary preferences', 
    link: '/dashboard', 
    done: !!user?.dietary_preference || !!user?.halal || !!user?.vegetarian 
  },
  { 
    title: 'Add your clubs or affiliations', 
    link: '/dashboard', 
    done: Array.isArray(user?.clubs) && user.clubs.length > 0 
  },
  { 
    title: 'Add your tech interests', 
    link: '/dashboard', 
    done: Array.isArray(user?.tech_interests) && user.tech_interests.length > 0 
  },
];

const actionItemsCompleted = actionItems.filter(item => item.done).length;
const actionItemsTotal = actionItems.length;
const personalCompleted = personalChecklist.filter(item => item.done).length;
const personalTotal = personalChecklist.length;
const totalCompleted = actionItemsCompleted + personalCompleted;
const totalItems = actionItemsTotal + personalTotal;

  const dietaryOptions = [
    { value: 'none', label: 'None' },
    { value: 'vegetarian', label: 'Vegetarian' },
    { value: 'vegan', label: 'Vegan' },
    { value: 'halal', label: 'Halal' },
    { value: 'kosher', label: 'Kosher' },
  ];

  const getDietaryLabel = (preference, profile = {}) => {
    if (preference) {
      const found = dietaryOptions.find(opt => opt.value === preference);
      if (found) return found.label;
    }
    if (profile.halal) return 'Halal';
    if (profile.vegetarian) return 'Vegetarian';
    return '';
  };

  const getMissingProfileFields = () => {
    if (!user) return [];
    const missing = [];
    if (!user.company) missing.push('School / Affiliation');
    if (!user.jobTitle && !user.job_title) missing.push('Role/Title');
    if (!user.bio) missing.push('Bio');
    if (!user.links) missing.push('Links');
    return missing;
  };

  const profileIncomplete = getMissingProfileFields().length > 0;

  // --- Personal checklist persistence (Supabase) ---
  useEffect(() => {
    const loadChecklist = async () => {
      if (!user) {
        setPersonalChecklist(defaultPersonalChecklist);
        return;
      }

      const { data, error } = await supabase
        .from('user_checklist')
        .select('item_key, title, done')
        .eq('user_id', user.id);

      if (error) {
        setPersonalChecklist(defaultPersonalChecklist);
        return;
      }

      if (!data || data.length === 0) {
        // Seed defaults
        const seedRows = defaultPersonalChecklist.map(item => ({
          user_id: user.id,
          item_key: item.key,
          title: item.title,
          done: false
        }));
        const { error: insertError, data: inserted } = await supabase
          .from('user_checklist')
          .insert(seedRows)
          .select('item_key, title, done');
        if (insertError) {
          setPersonalChecklist(defaultPersonalChecklist);
          return;
        }
        const merged = defaultPersonalChecklist.map(defaultItem => {
          const found = inserted.find(row => row.item_key === defaultItem.key);
          return found
            ? { key: found.item_key, title: found.title || defaultItem.title, done: !!found.done }
            : defaultItem;
        });
        setPersonalChecklist(merged);
        return;
      }

      // Merge fetched rows with defaults so missing items still show
      const merged = defaultPersonalChecklist.map(defaultItem => {
        const found = data.find(row => row.item_key === defaultItem.key);
        return found
          ? { key: found.item_key, title: found.title || defaultItem.title, done: !!found.done }
          : defaultItem;
      });
      setPersonalChecklist(merged);
    };

    loadChecklist();
  }, [user, defaultPersonalChecklist]);

  const togglePersonalItem = async (item, idx) => {
  const nextDone = !item.done;

  // Optimistic update
  setPersonalChecklist(list =>
    list.map((entry, i) =>
      i === idx ? { ...entry, done: nextDone } : entry
    )
  );

  if (!user) return;

  const { error } = await supabase
    .from('user_checklist')
    .upsert(
      {
        user_id: user.id,
        item_key: item.key,
        title: item.title,
        done: nextDone
      },
      { onConflict: 'user_id,item_key' }
    );

  if (error) {
    // revert on error
    setPersonalChecklist(list =>
      list.map((entry, i) =>
        i === idx ? { ...entry, done: !nextDone } : entry
      )
    );
  }
};

  // Update edit form when user data changes
  useEffect(() => {
    if (user) {
      const deriveDietaryPreference = (profile = {}) => {
        if (profile.dietary_preference) return profile.dietary_preference;
        if (profile.halal) return 'halal';
        if (profile.vegetarian) return 'vegetarian';
        return 'none';
      };

      setEditForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        jobTitle: user.jobTitle || user.job_title || '',
        bio: user.bio || '',
        links: user.links || '',
        clubs: user.clubs || [],
        tech_interests: user.tech_interests || [],
        dietary_preference: deriveDietaryPreference(user),
        avatar_url: user.avatar_url || '',
        public_profile: user.public_profile || false
      });
    }
  }, [user]);


  const quickActions = [
    {
      title: 'View Schedule',
      description: 'Check your personalized conference schedule',
      icon: <Calendar className="h-6 w-6" />,
      link: '/schedule',
      color: 'bg-blue-500'
    },
    {
      title: 'Browse Events',
      description: 'Explore all available conference events',
      icon: <Users className="h-6 w-6" />,
      link: '/events',
      color: 'bg-green-500'
    }
  ];

  

  const handleEditProfile = async () => {
    try {
      const updatedUser = await fetchUserProfile(user.id);

      if (updatedUser) {
        const deriveDietaryPreference = (profile = {}) => {
          if (profile.dietary_preference) return profile.dietary_preference;
          if (profile.halal) return 'halal';
          if (profile.vegetarian) return 'vegetarian';
          return 'none';
        };
        // New update

        setEditForm({
          name: updatedUser.name || '',
          email: updatedUser.email || '',
          phone: updatedUser.phone || '',
          company: updatedUser.company || '',
          jobTitle: updatedUser.jobTitle || updatedUser.job_title || '',
          bio: updatedUser.bio || '',
          links: updatedUser.links || '',
          clubs: updatedUser.clubs || [],
          tech_interests: updatedUser.tech_interests || [],
          dietary_preference: deriveDietaryPreference(updatedUser),
          avatar_url: updatedUser.avatar_url || '',
          public_profile: updatedUser.public_profile || false
        });

        setAvatarPreview(updatedUser.avatar_url || '');

      } else {
        const deriveDietaryPreference = (profile = {}) => {
          if (profile.dietary_preference) return profile.dietary_preference;
          if (profile.halal) return 'halal';
          if (profile.vegetarian) return 'vegetarian';
          return 'none';
        };

        setEditForm({
          name: user?.name || '',
          email: user?.email || '',
          phone: user?.phone || '',
          company: user?.company || '',
          jobTitle: user?.jobTitle || user?.job_title || '',
          bio: user?.bio || '',
          links: user?.links || '',
          clubs: user?.clubs || [],
          tech_interests: user?.tech_interests || [],
          dietary_preference: deriveDietaryPreference(user),
          avatar_url: user?.avatar_url || '',
          public_profile: user?.public_profile || false
        });

        setAvatarPreview(updatedUser.avatar_url || '');

      }
      setAvatarFile(null);
      setShowEditModal(true);
    } catch (error) {
      const deriveDietaryPreference = (profile = {}) => {
        if (profile.dietary_preference) return profile.dietary_preference;
        if (profile.halal) return 'halal';
        if (profile.vegetarian) return 'vegetarian';
        return 'none';
      };

      setEditForm({
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        company: user?.company || '',
        jobTitle: user?.jobTitle || user?.job_title || '',
        bio: user?.bio || '',
        links: user?.links || '',
        clubs: user?.clubs || [],
        tech_interests: user?.tech_interests || [],
        dietary_preference: deriveDietaryPreference(user),
        avatar_url: user?.avatar_url || '',
        public_profile: user?.public_profile || false
      });
      setShowEditModal(true);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      
      let avatarUrlToSave = editForm.avatar_url;

      if (avatarFile && user?.id) {
        setAvatarUploading(true);
        avatarUrlToSave = await uploadAvatar(avatarFile, user.id);
        setAvatarUploading(false);
      }

      const payload = {
        ...editForm,
        avatar_url: avatarUrlToSave
      }
      
      const result = await updateUser(payload);

      if (result.success) {
        setShowEditModal(false);
        setAvatarFile(null);
        setAvatarPreview('');

        alert('Profile updated successfully!');
      } else {
        alert(`Failed to update profile: ${result.message}`);
      }
    
    } catch (error) {
        // Supabase errors often come as { message, statusCode, error }
        const msg =
          error?.message ||
          error?.error_description ||
          (typeof error === 'string' ? error : JSON.stringify(error));

        alert(`Upload/update failed: ${msg}`);
} finally {
  setAvatarUploading(false);
  setLoading(false);
}

  };

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    if (name === 'dietary_preference') {
      setEditForm(prev => ({
        ...prev,
        dietary_preference: value
      }));
      return;
    }

    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  //  Updated club handlers
  const handleAddClub = (e) => {
    e.preventDefault();
    if (newClub.trim() && !editForm.clubs.includes(newClub.trim())) {
      setEditForm(prev => ({
        ...prev,
        clubs: [...prev.clubs, newClub.trim()]
      }));
    }
    setNewClub('');
  };

  const handleRemoveClub = (clubToRemove) => {
    setEditForm(prev => ({
      ...prev,
      clubs: prev.clubs.filter(club => club !== clubToRemove)
    }));
  };

  // Handle Add Tech
  const handleAddTech = (e) => {
    e.preventDefault();
    if (newTech.trim() && !editForm.tech_interests.includes(newTech.trim())) {
      setEditForm(prev => ({
        ...prev,
        tech_interests: [...prev.tech_interests, newTech.trim()]
      }));
      setNewTech('');
    }
  };

  const handleRemoveTech = (techToRemove) => {
    setEditForm(prev => ({
      ...prev,
      tech_interests: prev.tech_interests.filter(tech => tech !== techToRemove)
    }));
  };

  return (
    <div className="max-w-6xl mx-auto">

      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.name ? user.name.split(' ')[0] : ''}
        </h1>
        <p className="text-gray-600">Here's your conference dashboard and everything you need to know.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6 md:space-y-8">
          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quickActions.map((action, index) => (
                <Link
                  key={index}
                  to={action.link}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center mb-3">
                    <div className={`p-2 rounded-lg text-white mr-3 ${action.color}`}>
                      {action.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900">{action.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm">{action.description}</p>
                </Link>
              ))}

              
            </div>
          </div>

          {/* Progress Bar */}
          <div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div
                className="bg-green-500 h-2 rounded-full transition-all"
                style={{ width: `${totalItems ? (totalCompleted / totalItems) * 100 : 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {totalCompleted} of {totalItems} tasks completed
            </p>
          </div>
          {/* Upcoming Events */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">To Do List</h2>

            {/* Action Items moved to main column */}
            {user && actionItems.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                 Make sure to complete all of these tasks so you're ready for WECode 2026:
                </p>
                <ul className="space-y-2">
                  {actionItems.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                    >
                      <span className={`text-gray-700 ${item.done ? 'line-through text-gray-400' : ''}`}>
                        {item.title}
                      </span>
                      {item.done ? (
                        <span className="inline-flex items-center text-green-600 font-medium">
                          <CheckCircle className="h-4 w-4 mr-1" /> Done
                        </span>
                      ) : (
                        <Link to={item.link} className="text-rose-600 hover:text-rose-700 font-medium">
                          Complete
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Personal checklist (same UI, no label text) */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <ul className="space-y-2">
                {personalChecklist.map((item, idx) => (
                  <li key={item.key}

                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <span className={`text-gray-700 ${item.done ? 'line-through text-gray-400' : ''}`}>
                      {item.title}
                    </span>
                    {item.done ? (
                      <button
                        onClick={() => togglePersonalItem(item, idx)}
                        className="inline-flex items-center text-green-600 font-medium"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" /> Done
                      </button>
                    ) : (
                      <button
                        onClick={() => togglePersonalItem(item, idx)}
                        className="text-rose-600 hover:text-rose-700 font-medium"
                      >
                        Mark done
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Profile completeness notice */}
          {profileIncomplete && (
            <div className="card border-2 border-amber-200 bg-amber-50/80">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-900">Complete your profile</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Add your information so others can find you in the directory.
                  </p>
                  <button
                    onClick={handleEditProfile}
                    className="mt-3 inline-flex items-center px-3 py-1.5 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors"
                  >
                    Update Profile
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* User Profile */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h2>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-100 border flex items-center justify-center">
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-gray-600 font-semibold">
                    {getInitials(user?.name)}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center">
                <User className="h-4 w-4 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{user?.name}</p>
                </div>
              </div>
              {/* Email hidden */}
              {user?.links ? (
                <div className="flex items-start">
                  <Link2 className="h-4 w-4 text-gray-400 mr-3 mt-1" />
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Link to socials</p>
                    <a
                      href={/^https?:\/\//i.test(user.links) ? user.links : `https://${user.links}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary-700 hover:underline font-medium"
                    >
                      <span>{user.links.replace(/^https?:\/\//i, '')}</span>
                      <ArrowUpRightIcon className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="flex items-center">
                  <Link2 className="h-4 w-4 text-gray-300 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Link to socials</p>
                    <p className="text-sm text-gray-400">Not added</p>
                  </div>
                </div>
              )}

              {(() => {
                const label = getDietaryLabel(user?.dietary_preference, user);
                if (!label) return null;
                return (
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Dietary Preference</p>
                      <p className="font-medium">{label}</p>
                    </div>
                  </div>
                );
              })()}

              {Array.isArray(user?.clubs) && user.clubs.length > 0 && (
                <div className="flex items-start">
                  <Calendar className="h-4 w-4 text-gray-400 mr-3 mt-1" />
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Clubs</p>
                    <div className="flex flex-wrap gap-2">
                      {user.clubs.map((club) => (
                        <span key={club} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {club}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {Array.isArray(user?.tech_interests) && user.tech_interests.length > 0 && (
                <div className="flex items-start">
                  <Calendar className="h-4 w-4 text-gray-400 mr-3 mt-1" />
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Tech Interests</p>
                    <div className="flex flex-wrap gap-2">
                      {user.tech_interests.map((tech) => (
                        <span key={tech} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              

              {user?.bio && (
                <div className="flex items-start">
                  <Calendar className="h-4 w-4 text-gray-400 mr-3 mt-1" />
                  <div style={{ width: '200px', wordWrap: 'break-word' }}>
                    <p className="text-sm text-gray-500">Bio</p>
                    <p className="font-medium">{user?.bio || 'No bio provided.'}</p>
                  </div>
                </div>
              )}

              <button
                onClick={handleEditProfile}
                className="w-full btn-secondary mt-4"
              >
                Edit Profile
              </button>

            </div>
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Map</h2>
            <EventMap venues={venues} height="260px" />
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
    {showEditModal && (
  <div
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    onClick={() => setShowEditModal(false)} // click outside closes modal
  >
    <div
      className="flex flex-col bg-white rounded-xl p-6 w-full max-w-lg max-h-[85vh] shadow-xl"
      onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
    >
      <div className="flex-1 overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Edit Profile</h2>
          <button
            onClick={() => setShowEditModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

              <form onSubmit={handleSaveProfile} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />

                <input
                  type="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />

                {/* Profile Picture */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile picture
                  </label>

                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border">
                      {avatarPreview || editForm.avatar_url ? (
                        <img
                          src={avatarPreview || editForm.avatar_url}
                          alt="Profile"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-600 font-semibold">
                          {getInitials(editForm.name)}
                        </span>
                      )}
                    </div>

                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          // basic validation (optional)
                          if (file.size > 3 * 1024 * 1024) {
                            alert('Please upload an image under 3MB.');
                            return;
                          }

                          setAvatarFile(file);
                          setAvatarPreview(URL.createObjectURL(file));

                        }}
                        className="block w-full text-sm text-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        JPG/PNG recommended. Max 3MB.
                      </p>
                    </div>
                  </div>
                </div>


                <input
                  type="tel"
                  name="phone"
                  value={editForm.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />

                <input
                  type="text"
                  name="company"
                  value={editForm.company}
                  onChange={handleInputChange}
        placeholder="School / Affiliation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />

                <input
                  type="text"
                  name="jobTitle"
                  value={editForm.jobTitle}
                  onChange={handleInputChange}
                  placeholder="Job Title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />

                <input
                  type="text"
                  name="links"
                  value={editForm.links}
                  onChange={handleInputChange}
                  placeholder="Links"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />

                {/* Dietary Preferences */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dietary Preference
                  </label>
                  <select
                    name="dietary_preference"
                    value={editForm.dietary_preference || 'none'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {dietaryOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Choose one option: Kosher, Halal, Vegan, Vegetarian, or None.
                  </p>
                </div>

                {/* Privacy Setting */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="flex items-center cursor-pointer group">
                    <div className="relative flex items-center">
                      <input 
                        type="checkbox" 
                        name="public_profile" 
                        checked={editForm.public_profile || false} 
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-5 h-5 bg-white border-2 border-gray-300 rounded-md peer-checked:bg-primary-600 peer-checked:border-primary-600 transition-all duration-200 group-hover:border-primary-400 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 font-medium">
                      Show me in the Attendees Directory
                    </span>
                  </label>
                  <p className="ml-8 mt-1 text-xs text-gray-500">
                    Allow other attendees to see your profile in the directory
                  </p>
                </div>

                <textarea
                  rows="4"
                  name="bio"
                  value={editForm.bio}
                  onChange={handleInputChange}
                  placeholder="Short bio..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />  

                {/* Clubs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Affiliations (Organizations and Clubs)
                  </label>

                  <div className="flex flex-wrap gap-2 mb-2">
                    {editForm.clubs.map((club, index) => (
                      <span
                        key={index}
                        className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {club}
                        <button
                          type="button"
                          onClick={() => handleRemoveClub(club)}
                          className="ml-2 text-blue-500 hover:text-blue-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>

                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="clubInput"
                      value={newClub}
                      onChange={(e) => setNewClub(e.target.value)}
                      placeholder="Add club"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={handleAddClub}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Tech Interest */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tech Interest
                  </label>

                  <div className="flex flex-wrap gap-2 mb-2">
                    {editForm.tech_interests.map((tech, index) => (
                      <span
                        key={index}
                        className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {tech}
                        <button
                          type="button"
                          onClick={() => handleRemoveTech(tech)}
                          className="ml-2 text-blue-500 hover:text-blue-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>

                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="techInput"
                      value={newTech}
                      onChange={(e) => setNewTech(e.target.value)}
                      placeholder="Add Tech"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={handleAddTech}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || avatarUploading}
                    className="flex-1 btn-primary disabled:opacity-50"
                  >
                    {loading || avatarUploading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default Dashboard;
